import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper: Only log in development mode (prevents sensitive info leak in production)
const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => { if (isDev) console.log(...args); };
const devWarn = (...args) => { if (isDev) console.warn(...args); };

// Login function
async function loginAdmin(email, password) {
  devLog('Attempting login...');

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      devLog('Login error:', error.message);
      return { success: false, error: error.message };
    }

    if (!data.user) {
      devLog('No user returned from login');
      return { success: false, error: 'Login failed: No user data' };
    }

    devLog('Login successful');
    return { success: true, user: data.user, session: data.session };
  } catch (err) {
    console.error('Login exception:', err.message);
    return { success: false, error: err.message };
  }
}

// Logout function
async function logoutSupabase() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      devLog('Logout error:', error.message);
      return { success: false, error: error.message };
    }
    devLog('Logout successful');
    return { success: true };
  } catch (err) {
    console.error('Logout exception:', err.message);
    return { success: false, error: err.message };
  }
}

// ============================================================================
// USER CREATION (Admin only)
// ============================================================================

/**
 * Create a new user (Admin only)
 * Calls Edge Function to create user securely on server
 * @param {object} userData - { email, password, name, phone, role, status, group_id }
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function createUser(userData) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': supabaseAnonKey
      },
      body: JSON.stringify(userData)
    });

    const result = await response.json();

    if (!response.ok) {
      return { data: null, error: { message: result.error || 'Failed to create user' } };
    }

    return result;
  } catch (err) {
    console.error('Create user exception:', err);
    return { data: null, error: { message: err.message } };
  }
}

// ============================================================================
// PROFILE MANAGEMENT
// ============================================================================

/**
 * Get user profile by ID
 * @param {string} userId - User UUID
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*, groups(id, name, description, status)')
    .eq('id', userId)
    .single();

  return { data, error };
}

/**
 * Get all profiles (admin/manager only)
 * Relies on RLS policies to filter results:
 * - Admin sees all profiles
 * - Manager sees profiles in their group
 * - Regular users see nothing (only their own profile via getUserProfile)
 * @param {object} filters - Optional filters {role, status, group_id}
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getAllProfiles(filters = {}) {
  // Use standard client - RLS will handle access control based on JWT claims
  // Admin: sees all profiles
  // Manager: sees only profiles in their group
  let query = supabase
    .from('profiles')
    .select('*, groups(id, name, description, status)')
    .order('created_at', { ascending: false });

  if (filters.role) {
    query = query.eq('role', filters.role);
  }
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.group_id) {
    query = query.eq('group_id', filters.group_id);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Update user profile
 * @param {string} userId - User UUID
 * @param {object} updates - Fields to update
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function updateProfile(userId, updates) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  return { data, error };
}

// ============================================================================
// BALANCE MANAGEMENT
// ============================================================================

/**
 * Top-up user balance (Admin/Manager only)
 * @param {string} userId - User UUID to top-up
 * @param {number} amount - Amount to add
 * @param {string} notes - Optional notes
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function topupBalance(userId, amount, notes = null) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    const { data, error } = await supabase.rpc('topup_user_balance', {
      p_user_id: userId,
      p_amount: amount,
      p_performed_by: user.id,
      p_notes: notes
    });

    return { data, error };
  } catch (err) {
    return { data: null, error: { message: err.message } };
  }
}

/**
 * Get balance transactions for a user
 * @param {string} userId - User UUID
 * @param {number} limit - Number of transactions to fetch
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getBalanceTransactions(userId, limit = 50) {
  const { data, error } = await supabase
    .from('balance_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  return { data, error };
}

/**
 * Get all balance transactions (Admin/Manager only)
 * @param {object} filters - Optional filters {user_id, transaction_type, start_date, end_date}
 * @param {number} limit - Number of transactions to fetch
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getAllBalanceTransactions(filters = {}, limit = 100) {
  let query = supabase
    .from('balance_transactions')
    .select('*, profiles(id, name, email)')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (filters.user_id) {
    query = query.eq('user_id', filters.user_id);
  }
  if (filters.transaction_type) {
    query = query.eq('transaction_type', filters.transaction_type);
  }
  if (filters.start_date) {
    query = query.gte('created_at', filters.start_date);
  }
  if (filters.end_date) {
    query = query.lte('created_at', filters.end_date);
  }

  const { data, error } = await query;
  return { data, error };
}

// ============================================================================
// GROUP MANAGEMENT
// ============================================================================

/**
 * Get all groups
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getAllGroups() {
  const { data, error } = await supabase
    .from('groups')
    .select('*')
    .order('name', { ascending: true });

  return { data, error };
}

/**
 * Get a single group by ID
 * @param {string} groupId - Group UUID
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function getGroupById(groupId) {
  const { data, error } = await supabase
    .from('groups')
    .select('*')
    .eq('id', groupId)
    .single();

  return { data, error };
}

/**
 * Create a new group
 * @param {object} groupData - {name, description, status}
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function createGroup(groupData) {
  const { data, error } = await supabase
    .from('groups')
    .insert([groupData])
    .select()
    .single();

  return { data, error };
}

/**
 * Update a group (Admin only)
 * @param {string} groupId - Group UUID
 * @param {object} updates - Fields to update
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function updateGroup(groupId, updates) {
  const { data, error } = await supabase
    .from('groups')
    .update(updates)
    .eq('id', groupId)
    .select()
    .single();

  return { data, error };
}

/**
 * Delete a group (Admin only)
 * @param {string} groupId - Group UUID
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function deleteGroup(groupId) {
  const { data, error } = await supabase
    .from('groups')
    .delete()
    .eq('id', groupId);

  return { data, error };
}

// ============================================================================
// LOTTERY DRAW MANAGEMENT
// ============================================================================

/**
 * Get all lottery draws
 * @param {object} filters - Optional filters {status, start_date, end_date}
 * @param {number} limit - Number of draws to fetch
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getLotteryDraws(filters = {}, limit = 50) {
  let query = supabase
    .from('lottery_draws')
    .select('*')
    .order('draw_date', { ascending: false })
    .order('draw_time', { ascending: false })
    .limit(limit);

  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.start_date) {
    query = query.gte('draw_date', filters.start_date);
  }
  if (filters.end_date) {
    query = query.lte('draw_date', filters.end_date);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Get a single lottery draw by ID
 * @param {string} drawId - Draw UUID
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function getLotteryDraw(drawId) {
  const { data, error } = await supabase
    .from('lottery_draws')
    .select('*')
    .eq('id', drawId)
    .single();

  return { data, error };
}

/**
 * Create a lottery draw (Admin only)
 * @param {object} drawData - {draw_date, draw_time, special_prize, first_prize}
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function createLotteryDraw(drawData) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    const { data, error } = await supabase
      .from('lottery_draws')
      .insert([{ ...drawData, posted_by: user.id }])
      .select()
      .single();

    return { data, error };
  } catch (err) {
    return { data: null, error: { message: err.message } };
  }
}

/**
 * Update lottery draw results (Admin only)
 * @param {string} drawId - Draw UUID
 * @param {object} results - {special_prize, first_prize}
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function updateLotteryResults(drawId, results) {
  const { data, error } = await supabase
    .from('lottery_draws')
    .update(results)
    .eq('id', drawId)
    .select()
    .single();

  return { data, error };
}

/**
 * Check winners for a draw (Admin only)
 * @param {string} drawId - Draw UUID
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function checkWinners(drawId) {
  const { data, error } = await supabase.rpc('check_winners_for_draw', {
    p_draw_id: drawId
  });

  return { data, error };
}

// ============================================================================
// PURCHASE MANAGEMENT
// ============================================================================

/**
 * Create a purchase
 * @param {object} purchaseData - Purchase details
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function createPurchase(purchaseData) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    const { data, error } = await supabase.rpc('create_purchase', {
      p_transaction_id: purchaseData.transactionId,
      p_dealer_id: purchaseData.dealerId || user.id,
      p_customer_id: purchaseData.customerId || user.id,
      p_draw_id: purchaseData.drawId,
      p_number_purchased: purchaseData.numberPurchased,
      p_digit_type: purchaseData.digitType,
      p_prize_type: purchaseData.prizeType,
      p_amount: purchaseData.amount,
      p_payment_mode: purchaseData.paymentMode || 'balance'
    });

    return { data, error };
  } catch (err) {
    return { data: null, error: { message: err.message } };
  }
}

/**
 * Get purchases for a user (as customer or dealer)
 * @param {object} filters - Optional filters {dealer_id, customer_id, draw_id, status, start_date, end_date}
 * @param {number} limit - Number of purchases to fetch
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getPurchases(filters = {}, limit = 100) {
  let query = supabase
    .from('purchases')
    .select(`
      *,
      dealer:dealer_id(id, name, email),
      customer:customer_id(id, name, email),
      draw:draw_id(id, draw_date, draw_time, special_prize, first_prize)
    `)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (filters.dealer_id) {
    query = query.eq('dealer_id', filters.dealer_id);
  }
  if (filters.customer_id) {
    query = query.eq('customer_id', filters.customer_id);
  }
  if (filters.draw_id) {
    query = query.eq('draw_id', filters.draw_id);
  }
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.start_date) {
    query = query.gte('created_at', filters.start_date);
  }
  if (filters.end_date) {
    query = query.lte('created_at', filters.end_date);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Get all purchases (Admin/Manager only)
 * @param {object} filters - Optional filters
 * @param {number} limit - Number of purchases to fetch
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getAllPurchases(filters = {}, limit = 10000) {
  let query = supabase
    .from('purchases')
    .select(`
      *,
      dealer:dealer_id(id, name, email, group_id, groups(name)),
      customer:customer_id(id, name, email)
    `)
    .order('created_at', { ascending: false })
    .limit(limit);

  // Note: Flutter uses time_slot + purchase_date, NOT draw_id

  // Include deleted purchases for admin
  if (!filters.include_deleted) {
    query = query.is('deleted_at', null);
  }

  if (filters.dealer_id) {
    query = query.eq('dealer_id', filters.dealer_id);
  }
  if (filters.customer_id) {
    query = query.eq('customer_id', filters.customer_id);
  }
  if (filters.time_slot) {
    query = query.eq('time_slot', filters.time_slot);
  }
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  // Filter by purchase_date (Flutter's business date field)
  // Flutter already calculates: 20:01-23:59 → next day, 00:00-19:20 → current day
  if (filters.purchase_date) {
    devLog('Applying purchase_date filter:', filters.purchase_date);
    query = query.eq('purchase_date', filters.purchase_date);
  } else {
    if (filters.start_date) {
      query = query.gte('purchase_date', filters.start_date);
    }
    if (filters.end_date) {
      query = query.lte('purchase_date', filters.end_date);
    }
  }

  const { data, error } = await query;
  devLog('Query returned', data?.length, 'records');
  return { data, error };
}

/**
 * Delete a purchase (with 5-minute rule and refund)
 * @param {string} purchaseId - Purchase UUID
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function deletePurchase(purchaseId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    const { data, error } = await supabase.rpc('delete_purchase_with_refund', {
      p_purchase_id: purchaseId,
      p_user_id: user.id
    });

    return { data, error };
  } catch (err) {
    return { data: null, error: { message: err.message } };
  }
}

// ============================================================================
// REPORTING / VIEWS
// ============================================================================

/**
 * Get user sales summary (from view)
 * @param {object} filters - Optional filters {dealer_id, group_id}
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getUserSalesSummary(filters = {}) {
  let query = supabase
    .from('user_sales_summary')
    .select('*');

  if (filters.dealer_id) {
    query = query.eq('dealer_id', filters.dealer_id);
  }
  if (filters.group_id) {
    query = query.eq('group_id', filters.group_id);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Get daily sales report (from view)
 * @param {object} filters - Optional filters {start_date, end_date}
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getDailySalesReport(filters = {}) {
  let query = supabase
    .from('daily_sales_report')
    .select('*');

  if (filters.start_date) {
    query = query.gte('sale_date', filters.start_date);
  }
  if (filters.end_date) {
    query = query.lte('sale_date', filters.end_date);
  }

  const { data, error } = await query;
  return { data, error };
}

// ============================================================================
// DASHBOARD FUNCTIONS
// ============================================================================
// ⚡ OPTIMIZED: Uses single RPC call instead of 7+ parallel calls
// Run supabase/functions/get_dashboard_stats.sql to create the RPC function
// ============================================================================

/**
 * Purchase time-window logic:
 *
 * | Time of Day     | Business Date | Status  |
 * |-----------------|---------------|---------|
 * | 00:00 – 20:20   | Current day   | Allowed |
 * | 20:21 – 21:00   | —             | BLOCKED |
 * | 21:01 – 23:59   | Next day      | Allowed |
 *
 * Example: purchase at 2026-01-10 21:05 → business date '2026-01-11'
 *
 * Returns { status: 'current'|'next'|'blocked', businessDate: 'YYYY-MM-DD'|null }
 */
export function getPurchaseTimeStatus() {
  // Always use Vietnam timezone (UTC+7) regardless of browser/server locale
  const vn = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Ho_Chi_Minh' }));
  const totalMinutes = vn.getHours() * 60 + vn.getMinutes();

  const BLOCK_START    = 20 * 60 + 21; // 1221 = 20:21
  const NEXT_DAY_START = 21 * 60 + 1;  // 1261 = 21:01

  const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  if (totalMinutes >= NEXT_DAY_START) {
    const next = new Date(vn);
    next.setDate(next.getDate() + 1);
    return { status: 'next', businessDate: toDateStr(next) };
  }
  if (totalMinutes >= BLOCK_START) {
    return { status: 'blocked', businessDate: null };
  }
  return { status: 'current', businessDate: toDateStr(vn) };
}

function getBusinessDate() {
  return getPurchaseTimeStatus().businessDate;
}

/**
 * Get dashboard statistics - OPTIMIZED VERSION
 * Uses single RPC call instead of 7+ parallel calls
 * @param {string|null} groupId - Filter by group (null for all data - admin only)
 * @param {string|null} userId - Filter by user (for regular users to see only their data)
 * @param {string|null} targetDate - Optional date override (YYYY-MM-DD format), defaults to business date
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function getDashboardStats(groupId = null, userId = null, targetDate = null) {
  try {
    const dateToUse = targetDate || getBusinessDate();
    devLog('📅 Dashboard date:', dateToUse, targetDate ? '(custom)' : '(business date)');

    // Try optimized RPC first (single call)
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_dashboard_stats', {
      p_end_date: dateToUse,
      p_days: 7,
      p_group_id: groupId,
      p_user_id: userId
    });

    if (!rpcError && rpcData) {
      devLog('📊 Dashboard Stats (optimized RPC):', rpcData);
      return {
        data: {
          todaySales: parseFloat(rpcData.todaySales) || 0,
          weeklySales: parseFloat(rpcData.weeklySales) || 0,
          totalBalance: parseFloat(rpcData.totalBalance) || 0,
          totalRevenue: parseFloat(rpcData.totalRevenue) || 0,
          trend: rpcData.trend || []
        },
        error: null
      };
    }

    // Fallback to legacy implementation if RPC doesn't exist
    devWarn('⚠️ get_dashboard_stats RPC not found, using fallback. Run supabase/functions/get_dashboard_stats.sql');
    return getDashboardStatsLegacy(groupId, userId, dateToUse);
  } catch (error) {
    console.error('Dashboard stats error:', error);
    return { data: null, error };
  }
}

/**
 * Legacy dashboard stats implementation (fallback)
 * Uses multiple RPC calls - less efficient but works without the new function
 */
async function getDashboardStatsLegacy(groupId, userId, todayStr) {
  try {
    // Generate date strings for the last 7 days
    const businessDate = new Date(todayStr);
    const weeklyDates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(businessDate);
      d.setDate(d.getDate() - i);
      const dStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      weeklyDates.push(dStr);
    }

    // Use server-side RPC function for accurate aggregation
    const rpcCalls = weeklyDates.map(dateStr =>
      supabase.rpc('get_sales_by_dealer', { target_date: dateStr })
    );

    // Get balances query
    let balancesQuery = supabase.from('profiles').select('balance');
    if (userId) {
      balancesQuery = balancesQuery.eq('id', userId);
    } else if (groupId) {
      balancesQuery = balancesQuery.eq('group_id', groupId);
    }

    // Execute in parallel
    const [balancesRes, ...weeklyResults] = await Promise.all([
      balancesQuery,
      ...rpcCalls
    ]);

    // Helper function to filter data by user/group
    const filterData = (data) => {
      if (!data) return [];
      if (userId) return data.filter(d => d.dealer_id === userId);
      if (groupId) return data.filter(d => d.group_id === groupId);
      return data;
    };

    // Calculate today's sales
    let todaySales = 0;
    if (weeklyResults[0]?.data) {
      const todayData = filterData(weeklyResults[0].data);
      todaySales = todayData.reduce((sum, d) => sum + parseFloat(d.total_volume || 0), 0);
    }

    // Calculate weekly sales
    let weeklySales = 0;
    for (const result of weeklyResults) {
      if (result?.data) {
        const dayData = filterData(result.data);
        weeklySales += dayData.reduce((sum, d) => sum + parseFloat(d.total_volume || 0), 0);
      }
    }

    // Get total revenue from view
    let totalRevenueQuery = supabase
      .from('user_sales_summary')
      .select('total_amount');

    if (userId) totalRevenueQuery = totalRevenueQuery.eq('dealer_id', userId);
    else if (groupId) totalRevenueQuery = totalRevenueQuery.eq('group_id', groupId);

    const totalRevenueRes = await totalRevenueQuery;
    const totalRevenue = (totalRevenueRes.data || []).reduce((sum, d) => sum + parseFloat(d.total_amount || 0), 0);

    // Calculate total balance
    const totalBalance = (balancesRes.data || []).reduce((sum, p) => sum + parseFloat(p.balance || 0), 0);

    devLog('📊 Dashboard Stats (legacy):', { todaySales, weeklySales, totalRevenue, totalBalance });

    return {
      data: { todaySales, weeklySales, totalBalance, totalRevenue },
      error: null
    };
  } catch (error) {
    console.error('Dashboard stats legacy error:', error);
    return { data: null, error };
  }
}

/**
 * Get sales trend for last N days (for line chart)
 * OPTIMIZED: Uses single RPC call when available
 * @param {number} days - Number of days to fetch
 * @param {string|null} groupId - Filter by group (null for all data)
 * @param {string|null} userId - Filter by user (for regular users)
 * @param {string|null} targetDate - Optional date override (YYYY-MM-DD format), defaults to business date
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getSalesTrend(days = 7, groupId = null, userId = null, targetDate = null) {
  try {
    const dateToUse = targetDate || getBusinessDate();

    // Try optimized RPC first (includes trend data)
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_dashboard_stats', {
      p_end_date: dateToUse,
      p_days: days,
      p_group_id: groupId,
      p_user_id: userId
    });

    if (!rpcError && rpcData?.trend) {
      devLog('📈 Sales Trend (optimized RPC):', rpcData.trend);
      return { data: rpcData.trend, error: null };
    }

    // Fallback to legacy implementation
    devWarn('⚠️ Using legacy getSalesTrend');
    return getSalesTrendLegacy(days, groupId, userId, dateToUse);
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Legacy sales trend implementation (fallback)
 */
async function getSalesTrendLegacy(days, groupId, userId, endDateStr) {
  try {
    const endDate = new Date(endDateStr);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - days);

    // Generate date strings
    const dateStrings = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      dateStrings.push(dateStr);
    }

    // Multiple RPC calls
    const rpcCalls = dateStrings.map(dateStr =>
      supabase.rpc('get_sales_by_dealer', { target_date: dateStr })
    );

    const results = await Promise.all(rpcCalls);

    // Filter helper
    const filterData = (data) => {
      if (!data) return [];
      if (userId) return data.filter(d => d.dealer_id === userId);
      if (groupId) return data.filter(d => d.group_id === groupId);
      return data;
    };

    // Process results
    const data = dateStrings.map((dateStr, index) => {
      const result = results[index];
      let sales = 0;
      if (result?.data) {
        const filteredData = filterData(result.data);
        sales = filteredData.reduce((sum, d) => sum + parseFloat(d.total_volume || 0), 0);
      }
      return { date: dateStr, sales };
    });

    devLog('📈 Sales Trend (legacy):', data);
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Get top dealers by total sales amount
 * @param {number} limit - Number of top dealers to fetch
 * @param {string|null} groupId - Filter by group (null for all data)
 * @param {string|null} userId - Filter by user (for regular users - returns only their data)
 * @param {array|null} groupIds - Filter by multiple groups (for super managers)
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getTopDealers(limit = 5, groupId = null, userId = null, groupIds = null) {
  try {
    let query = supabase
      .from('user_sales_summary')
      .select('dealer_id, dealer_name, total_amount, total_tickets, group_name')
      .order('total_amount', { ascending: false })
      .limit(limit);

    if (userId) {
      // Regular user only sees their own data
      query = query.eq('dealer_id', userId);
    } else if (groupIds && groupIds.length > 0) {
      // Super manager sees dealers from their assigned groups
      query = query.in('group_id', groupIds);
    } else if (groupId) {
      // Manager sees their group's dealers
      query = query.eq('group_id', groupId);
    }
    // Admin sees all (no filter)

    const { data, error } = await query;
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
}

// ============================================================================
// PASSWORD RESET (Admin only)
// ============================================================================

/**
 * Reset user password (Admin only)
 * Uses Supabase Auth Admin API to update user password
 * @param {string} userId - The user's UUID
 * @param {string} newPassword - The new password
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function resetUserPassword(userId, newPassword) {
  try {
    // Validate inputs (client-side validation for quick feedback)
    if (!userId) {
      return { data: null, error: { message: "User ID is required" } };
    }

    if (!newPassword || newPassword.length < 8) {
      return { data: null, error: { message: "Password must be at least 8 characters" } };
    }

    const hasLetter = /[a-zA-Z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    if (!hasLetter || !hasNumber) {
      return { data: null, error: { message: "Password must contain at least one letter and one number" } };
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    devLog('Attempting to reset password for user:', userId);

    const response = await fetch(`${supabaseUrl}/functions/v1/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': supabaseAnonKey
      },
      body: JSON.stringify({ userId, newPassword })
    });

    const result = await response.json();

    if (!response.ok) {
      return { data: null, error: { message: result.error || 'Failed to reset password' } };
    }

    devLog('Password reset successful for user:', userId);
    return result;
  } catch (err) {
    console.error('resetUserPassword error:', err);
    return { data: null, error: { message: err.message || "An unexpected error occurred" } };
  }
}

// ============================================================================
// LOTTERY WINNER CALCULATION (Display Only - No Auto Topup)
// ============================================================================

/**
 * Prize configuration
 * 2-digit: 1,000 -> 90,000 (90x multiplier)
 * 3-digit: 1,000 -> 650,000 (650x multiplier)
 */
const PRIZE_CONFIG = {
  '2D': {
    multiplier: 90,
    base_bet: 1000,
    prize: 90000
  },
  '3D': {
    multiplier: 650,
    base_bet: 1000,
    prize: 650000
  }
};

/**
 * Check if a purchase ticket is a winner
 * Uses prize_type to determine which winning number to check against
 * Uses digit_type to determine 2D or 3D matching
 * @param {object} purchase - Purchase record with number_purchased, digit_type, prize_type
 * @param {object} draw - Draw record with special_last_2, special_last_3, first_last_2, first_last_3
 * @returns {object|null} - Win info or null if not a winner
 */
function checkPurchaseWin(purchase, draw) {
  if (!purchase || !draw) return null;
  
  const ticket = purchase.number_purchased?.toString().trim();
  const digitType = purchase.digit_type; // '2D' or '3D'
  const prizeType = purchase.prize_type; // 'special' or 'first'
  
  if (!ticket || !digitType || !prizeType) return null;
  
  let winningNumber = null;
  let prizeName = '';
  
  // Determine which winning number to check based on prize_type and digit_type
  if (prizeType === 'special') {
    if (digitType === '2D') {
      winningNumber = draw.special_last_2;
      prizeName = 'Special Prize (Last 2)';
    } else if (digitType === '3D') {
      winningNumber = draw.special_last_3;
      prizeName = 'Special Prize (Last 3)';
    }
  } else if (prizeType === 'first') {
    if (digitType === '2D') {
      winningNumber = draw.first_last_2;
      prizeName = 'First Prize (Last 2)';
    } else if (digitType === '3D') {
      winningNumber = draw.first_last_3;
      prizeName = 'First Prize (Last 3)';
    }
  }
  
  if (!winningNumber) return null;
  
  // Check if ticket matches winning number
  if (ticket === winningNumber.toString().trim()) {
    const config = PRIZE_CONFIG[digitType];
    return {
      is_winner: true,
      prize_name: prizeName,
      digit_type: digitType,
      prize_type: prizeType,
      multiplier: config?.multiplier || 0,
      winning_number: winningNumber
    };
  }
  
  return null;
}

/**
 * Calculate prize amount based on bet amount and digit type
 * @param {number} betAmount - Amount bet on the ticket
 * @param {string} digitType - '2D' or '3D'
 * @returns {number} - Prize amount
 */
function calculatePrizeAmount(betAmount, digitType) {
  const config = PRIZE_CONFIG[digitType];
  if (!config) return 0;
  
  // Prize = (betAmount / base_bet) * prize
  // e.g., if bet 2000 on 2D: (2000 / 1000) * 80000 = 160,000
  return (betAmount / config.base_bet) * config.prize;
}

/**
 * Check winner status for a single purchase against draw data
 * This is used in the UI to display winner status
 * @param {object} purchase - Purchase object with number_purchased, digit_type, prize_type
 * @param {object} draw - Draw object with winning numbers
 * @returns {object} - Object with is_winner, prize_name, calculated_prize, etc.
 */
export function checkWinnerStatus(purchase, draw) {
  if (!purchase || !draw) {
    return { is_winner: false };
  }
  
  const winInfo = checkPurchaseWin(purchase, draw);
  const betAmount = parseFloat(purchase.amount_purchased) || 0;
  const calculatedPrize = winInfo ? calculatePrizeAmount(betAmount, purchase.digit_type) : 0;
  
  return {
    is_winner: !!winInfo,
    prize_name: winInfo?.prize_name || null,
    winning_number: winInfo?.winning_number || null,
    calculated_prize: calculatedPrize,
    multiplier: winInfo?.multiplier || null
  };
}

/**
 * Get all purchases for a draw with winner status
 * @param {string} drawId - Draw UUID
 * @param {string|null} groupId - Filter by group (for manager)
 * @param {string|null} userId - Filter by user (for dealer)
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getDrawPurchasesWithWinnerStatus(drawId, groupId = null, userId = null) {
  try {
    // Get the draw's winning numbers
    const { data: draw, error: drawError } = await supabase
      .from('lottery_draws')
      .select('id, draw_date, draw_time, special_prize, first_prize, special_last_3, special_last_2, first_last_3, first_last_2, status, winners_checked')
      .eq('id', drawId)
      .single();

    if (drawError) throw drawError;

    // Get purchases for this draw
    let query = supabase
      .from('purchases')
      .select(`
        id,
        transaction_id,
        dealer_id,
        customer_id,
        draw_id,
        number_purchased,
        digit_type,
        prize_type,
        amount_purchased,
        potential_winning,
        status,
        actual_winning,
        win_claimed,
        created_at,
        dealer:dealer_id (
          id,
          name,
          email,
          phone,
          group_id
        ),
        customer:customer_id (
          id,
          name,
          email,
          phone
        )
      `)
      .eq('draw_id', drawId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    // Apply user filter
    if (userId) {
      query = query.eq('dealer_id', userId);
    }

    const { data: purchases, error: purchasesError } = await query;
    if (purchasesError) throw purchasesError;

    // Filter by group if needed
    let filteredPurchases = purchases || [];
    if (groupId && !userId) {
      filteredPurchases = filteredPurchases.filter(p => 
        p.dealer?.group_id === groupId
      );
    }

    // Check winner status for each purchase
    const purchasesWithStatus = filteredPurchases.map(purchase => {
      const winInfo = checkPurchaseWin(purchase, draw);
      const betAmount = parseFloat(purchase.amount_purchased) || 0;
      const calculatedPrize = winInfo ? calculatePrizeAmount(betAmount, purchase.digit_type) : 0;
      
      return {
        ...purchase,
        is_winner: !!winInfo,
        prize_name: winInfo?.prize_name || null,
        winning_number: winInfo?.winning_number || null,
        calculated_prize: calculatedPrize,
        multiplier: winInfo?.multiplier || null
      };
    });

    return { data: purchasesWithStatus, error: null };
  } catch (error) {
    console.error('Error fetching purchases with winner status:', error);
    return { data: null, error };
  }
}

/**
 * Get only winning tickets for a specific draw
 * @param {string} drawId - Draw UUID
 * @param {string|null} groupId - Filter by group
 * @param {string|null} userId - Filter by user
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getWinningTickets(drawId, groupId = null, userId = null) {
  try {
    const { data: allPurchases, error } = await getDrawPurchasesWithWinnerStatus(drawId, groupId, userId);
    
    if (error) throw error;
    
    // Filter to only winners
    const winners = (allPurchases || []).filter(p => p.is_winner);
    
    return { data: winners, error: null };
  } catch (error) {
    console.error('Error fetching winning tickets:', error);
    return { data: null, error };
  }
}

/**
 * Update purchase status after checking winners (NO auto-topup)
 * Only updates status to 'won' or 'lost' and sets actual_winning
 * Does NOT add to balance - that requires manual topup
 * @param {string} drawId - Draw UUID
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function checkAndUpdateWinners(drawId) {
  try {
    // Get draw details
    const { data: draw, error: drawError } = await supabase
      .from('lottery_draws')
      .select('*')
      .eq('id', drawId)
      .single();

    if (drawError) throw drawError;
    if (!draw) throw new Error('Draw not found');

    // Check if draw has results
    if (!draw.special_last_2 && !draw.special_last_3 && !draw.first_last_2 && !draw.first_last_3) {
      throw new Error('Draw results not yet posted');
    }

    // Get all pending purchases for this draw
    const { data: purchases, error: purchasesError } = await supabase
      .from('purchases')
      .select('*')
      .eq('draw_id', drawId)
      .eq('status', 'pending')
      .is('deleted_at', null);

    if (purchasesError) throw purchasesError;

    let winnersCount = 0;
    let losersCount = 0;
    let totalWinningAmount = 0;
    const winners = [];

    // Process each purchase
    for (const purchase of purchases || []) {
      const winInfo = checkPurchaseWin(purchase, draw);
      const betAmount = parseFloat(purchase.amount_purchased) || 0;
      
      if (winInfo) {
        // Winner - update status and actual_winning (NO balance topup)
        const prizeAmount = calculatePrizeAmount(betAmount, purchase.digit_type);
        
        const { error: updateError } = await supabase
          .from('purchases')
          .update({
            status: 'won',
            actual_winning: prizeAmount,
            win_claimed: false // Must be manually claimed/paid
          })
          .eq('id', purchase.id);

        if (updateError) {
          console.error(`Failed to update winner ${purchase.id}:`, updateError);
        } else {
          winnersCount++;
          totalWinningAmount += prizeAmount;
          winners.push({
            ...purchase,
            prize_amount: prizeAmount,
            prize_name: winInfo.prize_name
          });
        }
      } else {
        // Loser - update status
        const { error: updateError } = await supabase
          .from('purchases')
          .update({
            status: 'lost',
            actual_winning: 0
          })
          .eq('id', purchase.id);

        if (!updateError) {
          losersCount++;
        }
      }
    }

    // Mark draw as winners_checked
    await supabase
      .from('lottery_draws')
      .update({ winners_checked: true })
      .eq('id', drawId);

    return {
      data: {
        success: true,
        winners_count: winnersCount,
        losers_count: losersCount,
        total_winning_amount: totalWinningAmount,
        winners: winners,
        message: 'Winners identified. Manual payment required to claim prizes.'
      },
      error: null
    };
  } catch (error) {
    console.error('Error checking winners:', error);
    return { data: null, error };
  }
}

/**
 * Get all unclaimed winners (for admin to pay out)
 * @param {string|null} groupId - Filter by group
 * @param {string|null} userId - Filter by user
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getUnclaimedWinners(groupId = null, userId = null) {
  try {
    let query = supabase
      .from('purchases')
      .select(`
        id,
        transaction_id,
        dealer_id,
        customer_id,
        draw_id,
        number_purchased,
        digit_type,
        prize_type,
        amount_purchased,
        actual_winning,
        win_claimed,
        status,
        created_at,
        dealer:dealer_id (
          id,
          name,
          email,
          phone,
          balance,
          group_id
        ),
        customer:customer_id (
          id,
          name,
          email,
          phone
        ),
        draw:draw_id (
          id,
          draw_date,
          draw_time
        )
      `)
      .eq('status', 'won')
      .eq('win_claimed', false)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (userId) {
      query = query.eq('dealer_id', userId);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Filter by group if needed
    let filteredData = data || [];
    if (groupId && !userId) {
      filteredData = filteredData.filter(p => p.dealer?.group_id === groupId);
    }

    return { data: filteredData, error: null };
  } catch (error) {
    console.error('Error fetching unclaimed winners:', error);
    return { data: null, error };
  }
}

/**
 * Pay a winner - manually top up balance and mark as claimed
 * This is the ONLY function that adds prize money to balance
 * @param {string} purchaseId - Purchase ID
 * @param {string} adminUserId - Admin who is approving payment
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function payWinner(purchaseId, adminUserId) {
  try {
    // Get the winning purchase
    const { data: purchase, error: purchaseError } = await supabase
      .from('purchases')
      .select(`
        id,
        dealer_id,
        customer_id,
        actual_winning,
        win_claimed,
        status,
        number_purchased,
        digit_type,
        prize_type
      `)
      .eq('id', purchaseId)
      .single();

    if (purchaseError) throw purchaseError;
    if (!purchase) throw new Error('Purchase not found');
    if (purchase.status !== 'won') throw new Error('This purchase is not a winner');
    if (purchase.win_claimed) throw new Error('Prize already claimed');

    const prizeAmount = parseFloat(purchase.actual_winning);
    if (prizeAmount <= 0) throw new Error('Invalid prize amount');

    // Determine who gets the prize (customer or dealer)
    const recipientId = purchase.customer_id || purchase.dealer_id;
    if (!recipientId) throw new Error('No recipient found');

    // Top up the recipient's balance
    const { data: topupData, error: topupError } = await topupBalance(
      recipientId,
      prizeAmount,
      `Lottery prize: ${purchase.digit_type} ${purchase.prize_type} - Ticket: ${purchase.number_purchased}`
    );

    if (topupError) {
      throw new Error(`Failed to top up balance: ${topupError.message}`);
    }

    // Mark as claimed
    const { data: updateData, error: updateError } = await supabase
      .from('purchases')
      .update({
        win_claimed: true
      })
      .eq('id', purchaseId)
      .select()
      .single();

    if (updateError) {
      console.error('CRITICAL: Balance topped up but win_claimed not updated:', purchaseId);
      throw updateError;
    }

    return {
      data: {
        success: true,
        purchase: updateData,
        topup: topupData,
        prize_amount: prizeAmount,
        recipient_id: recipientId,
        message: `Successfully paid ${prizeAmount.toLocaleString()} to winner`
      },
      error: null
    };
  } catch (error) {
    console.error('Error paying winner:', error);
    return { data: null, error };
  }
}

/**
 * Get draw summary with revenue split calculation
 * @param {string} drawId - Draw UUID
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function getDrawSummary(drawId) {
  try {
    // Get draw info
    const { data: draw, error: drawError } = await supabase
      .from('lottery_draws')
      .select('*')
      .eq('id', drawId)
      .single();

    if (drawError) throw drawError;

    // Get all purchases for this draw
    const { data: purchases, error: purchasesError } = await supabase
      .from('purchases')
      .select('id, number_purchased, digit_type, prize_type, amount_purchased, status, actual_winning, win_claimed')
      .eq('draw_id', drawId)
      .is('deleted_at', null);

    if (purchasesError) throw purchasesError;

    // Calculate totals
    let totalSales = 0;
    let total3D = 0;
    let total2D = 0;
    let totalWinningAmount = 0;
    let totalClaimed = 0;
    let totalUnclaimed = 0;
    const winners = [];

    (purchases || []).forEach(purchase => {
      const betAmount = parseFloat(purchase.amount_purchased) || 0;
      totalSales += betAmount;
      
      if (purchase.digit_type === '3D') total3D += betAmount;
      if (purchase.digit_type === '2D') total2D += betAmount;
      
      if (purchase.status === 'won') {
        const prizeAmount = parseFloat(purchase.actual_winning) || 0;
        totalWinningAmount += prizeAmount;
        
        if (purchase.win_claimed) {
          totalClaimed += prizeAmount;
        } else {
          totalUnclaimed += prizeAmount;
        }
        
        winners.push(purchase);
      }
    });

    // Check if this is a manual draw (16:00 or 20:30) - exclude from prize pool
    const drawTime = draw.draw_time?.substring(0, 5);
    const isManualDraw = drawTime === '16:00' || drawTime === '20:30';

    // Revenue split: You 40%, Dealer 30%, Prize Pool 30%
    // Manual draws (16:00/20:30) are external results - no prize pool split
    const yourShare = isManualDraw ? 0 : totalSales * 0.40;
    const dealerShare = isManualDraw ? 0 : totalSales * 0.30;
    const prizePool = isManualDraw ? 0 : totalSales * 0.30;

    // Check if prize pool can cover winnings (not applicable for manual draws)
    const prizePoolSufficient = isManualDraw ? null : prizePool >= totalWinningAmount;
    const profitLoss = isManualDraw ? null : prizePool - totalWinningAmount;

    return {
      data: {
        draw,
        summary: {
          total_sales: totalSales,
          total_3d_sales: total3D,
          total_2d_sales: total2D,
          total_tickets: purchases?.length || 0,
          your_share: yourShare,
          dealer_share: dealerShare,
          prize_pool: prizePool,
          total_winning_amount: totalWinningAmount,
          total_claimed: totalClaimed,
          total_unclaimed: totalUnclaimed,
          prize_pool_sufficient: prizePoolSufficient,
          profit_loss: profitLoss,
          winners_count: winners.length,
          is_manual_draw: isManualDraw
        },
        winners
      },
      error: null
    };
  } catch (error) {
    console.error('Error fetching draw summary:', error);
    return { data: null, error };
  }
}

// ============================================================================
// NUMBER VOLUME TRACKING (High-volume numbers for 16:00 & 20:30)
// ============================================================================

/**
 * Get top purchased numbers for a specific time slot and date
 * Groups by number_purchased with breakdown by prize_type (special/first)
 * @param {string} timeSlot - Time slot to filter in HH:MM format (e.g., '16:00' or '20:30')
 * @param {string} purchaseDate - Date in YYYY-MM-DD format
 * @param {number} limit - Max numbers to return (default 50)
 * @returns {Promise<{data: array|null, error: object|null}>}
 */
export async function getHighVolumeNumbers(timeSlot, purchaseDate, limit = 50, digitType = null) {
  try {
    // Pagination to bypass Supabase 1000 row default limit
    let allData = [];
    let hasMore = true;
    let offset = 0;
    const pageSize = 1000;

    while (hasMore) {
      let query = supabase
        .from('purchases')
        .select('number_purchased, amount_purchased, digit_type, prize_type')
        .eq('time_slot', timeSlot)
        .eq('purchase_date', purchaseDate)
        .is('deleted_at', null)
        .range(offset, offset + pageSize - 1);

      if (digitType) {
        query = query.eq('digit_type', digitType);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data && data.length > 0) {
        allData = allData.concat(data);
        offset += pageSize;
        hasMore = data.length === pageSize;
      } else {
        hasMore = false;
      }
    }

    // Aggregate client-side: group by number_purchased with prize_type breakdown
    const numberMap = {};
    allData.forEach(row => {
      const num = row.number_purchased;
      if (!numberMap[num]) {
        numberMap[num] = {
          number: num,
          count: 0,
          totalAmount: 0,
          digitType: row.digit_type,
          specialCount: 0,
          specialAmount: 0,
          firstCount: 0,
          firstAmount: 0,
        };
      }
      const amt = parseFloat(row.amount_purchased) || 0;
      numberMap[num].count += 1;
      numberMap[num].totalAmount += amt;

      if (row.prize_type === 'special') {
        numberMap[num].specialCount += 1;
        numberMap[num].specialAmount += amt;
      } else if (row.prize_type === 'first') {
        numberMap[num].firstCount += 1;
        numberMap[num].firstAmount += amt;
      }
    });

    // Sort by total AMOUNT descending (high volume by money), take top N
    const sorted = Object.values(numberMap)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, limit);

    return { data: sorted, error: null };
  } catch (error) {
    console.error('Error fetching high volume numbers:', error);
    return { data: null, error };
  }
}

// ============================================================================
// SUPER MANAGER GROUP MANAGEMENT
// ============================================================================

/**
 * Get groups assigned to a super manager
 * @param {string} userId - Super manager's user ID
 * @returns {Promise<{data: array, error: object|null}>}
 */
export async function getSuperManagerGroups(userId) {
  try {
    const { data, error } = await supabase
      .from('super_manager_groups')
      .select(`
        id,
        group_id,
        groups (
          id,
          name
        )
      `)
      .eq('user_id', userId);

    if (error) throw error;
    return { data: data?.map(item => item.groups).filter(Boolean) || [], error: null };
  } catch (error) {
    console.error('Error fetching super manager groups:', error);
    return { data: [], error };
  }
}

/**
 * Assign groups to a super manager (admin only)
 * @param {string} userId - Super manager's user ID
 * @param {array} groupIds - Array of group IDs to assign
 * @param {string} assignedBy - Admin user ID who assigned
 * @returns {Promise<{success: boolean, error: object|null}>}
 */
export async function assignGroupsToSuperManager(userId, groupIds, assignedBy) {
  try {
    // Remove existing assignments
    const { error: deleteError } = await supabase
      .from('super_manager_groups')
      .delete()
      .eq('user_id', userId);

    if (deleteError) throw deleteError;

    // Add new assignments
    if (groupIds.length > 0) {
      const assignments = groupIds.map(groupId => ({
        user_id: userId,
        group_id: groupId,
        assigned_by: assignedBy
      }));

      const { error: insertError } = await supabase
        .from('super_manager_groups')
        .insert(assignments);

      if (insertError) throw insertError;
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error assigning groups to super manager:', error);
    return { success: false, error };
  }
}

/**
 * Get users in groups managed by super manager
 * @param {string} userId - Super manager's user ID
 * @returns {Promise<{data: array, error: object|null}>}
 */
export async function getUsersForSuperManager(userId) {
  try {
    const { data: groups, error: groupsError } = await getSuperManagerGroups(userId);
    
    if (groupsError) throw groupsError;
    
    const groupIds = groups.map(g => g.id);
    
    if (groupIds.length === 0) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*, groups(*)')
      .in('group_id', groupIds);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching users for super manager:', error);
    return { data: [], error };
  }
}

// ─── Gemini training examples ─────────────────────────────────────────────────

export async function getAllParserExamples() {
  return supabase.from('parser_examples').select('*').order('created_at', { ascending: false });
}

export async function upsertParserExample(row) {
  return supabase.from('parser_examples').upsert(row).select().single();
}

export async function deleteParserExample(id) {
  return supabase.from('parser_examples').delete().eq('id', id);
}

export async function uploadTrainingPhoto(file) {
  // Ensure bucket exists — no-op if already created
  await supabase.storage.createBucket('training-photos', { public: true, fileSizeLimit: 5242880 });
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from('training-photos').upload(path, file, { contentType: file.type });
  if (error) return { error };
  const { data: { publicUrl } } = supabase.storage.from('training-photos').getPublicUrl(path);
  return { url: publicUrl };
}

export async function getPhotoReviews({ reviewed = false } = {}) {
  return supabase.from('photo_reviews')
    .select('*, profiles(name)')
    .eq('reviewed', reviewed)
    .order('created_at', { ascending: false });
}

export async function markPhotoReviewed(id, imageUrl) {
  if (imageUrl) await deleteTrainingPhoto(imageUrl);
  return supabase.from('photo_reviews').update({ reviewed: true }).eq('id', id);
}

export async function deletePhotoReview(id, imageUrl) {
  if (imageUrl) await deleteTrainingPhoto(imageUrl);
  return supabase.from('photo_reviews').delete().eq('id', id);
}

async function learnDealerCorrections(dealerId, rawOcrText, correctedOutput) {
  if (!dealerId || !rawOcrText || !correctedOutput) return;
  const rawLines = rawOcrText.trim().split('\n');
  const corrLines = correctedOutput.trim().split('\n');
  for (let i = 0; i < Math.min(rawLines.length, corrLines.length); i++) {
    const rNum = rawLines[i].match(/^(\d+)/)?.[1];
    const cNum = corrLines[i].match(/^(\d+)/)?.[1];
    if (rNum && cNum && rNum !== cNum) {
      await supabase.from('dealer_ocr_corrections').upsert(
        { dealer_id: dealerId, wrong_text: rNum, correct_text: cNum, count: 1, updated_at: new Date().toISOString() },
        { onConflict: 'dealer_id,wrong_text' }
      );
      await supabase.rpc('increment_ocr_correction_count', { p_dealer_id: dealerId, p_wrong: rNum });
    }
  }
}

export async function saveReviewAsTrainingExample({ imageUrl, rawOcrText, correctedOutput, dealerId }) {
  const ticketCount = (correctedOutput || '').split('\n').filter((l) => l.includes('=')).length;
  const result = await supabase.from('parser_examples').insert({
    raw_text: rawOcrText || '',
    reformatted: correctedOutput,
    ticket_count: ticketCount,
    image_url: imageUrl,
    pinned: true,
    ...(dealerId && { dealer_id: dealerId }),
  });
  await learnDealerCorrections(dealerId, rawOcrText, correctedOutput);
  return result;
}

export async function deleteTrainingPhoto(imageUrl) {
  if (!imageUrl) return;
  const path = imageUrl.split('/training-photos/')[1];
  if (!path) return;
  return supabase.storage.from('training-photos').remove([path]);
}

export default supabase;
export { supabase, loginAdmin, logoutSupabase };